import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { useToast } from "../../components/ui";
import { Header } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import constant, { statuses } from "../../constants";
import { resizeImage, validate } from "../../util";

// Icons
import { GrBike } from "react-icons/gr";
import {
  FaPlus,
  FaCheck,
  FaXmark,
  FaGift,
  FaPenToSquare,
  FaTrashCan,
  FaMagnifyingGlass,
  FaCircleCheck,
  FaCircleExclamation,
  FaLayerGroup,
  FaTicket,
  FaArrowRotateRight,
  FaCopy,
} from "react-icons/fa6";
import { TbPhotoPlus, TbSparkles } from "react-icons/tb";

// ── Mission Types Definition & Helpers ──────────────────────────────────────────

const MISSION_TYPES = [
  {
    value: "amountInOneOrder",
    label: "จำนวนใบใน 1 ออเดอร์",
    desc: "ซื้อสลากกี่ใบก็ได้ครบตามจำนวนที่กำหนดใน 1 คำสั่งซื้อ",
    icon: "🎯",
  },
  {
    value: "lastTwoInOneOrder",
    label: "ระบุเลขท้าย 2 ตัว (1 ออเดอร์)",
    desc: "ซื้อสลากที่ลงท้ายด้วยเลข xx ให้ครบในคำสั่งซื้อเดียว",
    icon: "🔢",
  },
  {
    value: "lastTwoInMultipleOrders",
    label: "ระบุเลขท้าย 2 ตัว (สะสมหลายออเดอร์)",
    desc: "ซื้อสลากเลขท้าย xx สะสมรวมกันหลายคำสั่งซื้อ",
    icon: "🔄",
  },
  {
    value: "setInMultipleOrder",
    label: "หวยชุด (สะสมหลายออเดอร์)",
    desc: "ซื้อสลากแบบชุด (เช่น ชุด 2 ใบ, 5 ใบ) ให้ครบจำนวนชุด",
    icon: "📦",
  },
  {
    value: "custom",
    label: "ภารกิจกำหนดเอง (Custom)",
    desc: "ภารกิจพิเศษ กำหนดเงื่อนไขและรายละเอียดเอง",
    icon: "⚙️",
  },
];

const SET_OPTIONS = [
  { value: 2, label: "ชุด 2 ใบ" },
  { value: 3, label: "ชุด 3 ใบ" },
  { value: 4, label: "ชุด 4 ใบ" },
  { value: 5, label: "ชุด 5 ใบ" },
  { value: 6, label: "ชุด 6 ใบ" },
  { value: 7, label: "ชุด 7 ใบ" },
  { value: 8, label: "ชุด 8 ใบ" },
  { value: 9, label: "ชุด 9 ใบ" },
  { value: 10, label: "ชุด 10 ใบ" },
];

const getMissionConditionSummary = (mission) => {
  if (!mission || !mission.type) return "ยังไม่ได้กำหนดเงื่อนไข";
  switch (mission.type) {
    case "amountInOneOrder":
      return `ซื้อสลาก ${mission.requiredLotto || 0} ใบ ใน 1 ออเดอร์`;
    case "lastTwoInOneOrder":
      return `ซื้อเลขท้าย "${mission.lastTwo || "--"}" จำนวน ${mission.requiredLotto || 0} ใบ ใน 1 ออเดอร์`;
    case "lastTwoInMultipleOrders":
      return `ซื้อเลขท้าย "${mission.lastTwo || "--"}" รวม ${mission.requiredLotto || 0} ใบ (${mission.requiredOrder || 1} ออเดอร์)`;
    case "setInMultipleOrder": {
      const setSize = mission.set || 2;
      const setQty = mission.requiredSet || 0;
      const total = setSize * setQty;
      return `ซื้อหวยชุด ${setSize} ใบ จำนวน ${setQty} ชุด (รวม ${total} ใบ)`;
    }
    case "custom":
      return mission.info ? `ภารกิจพิเศษ: ${mission.info}` : "ภารกิจกำหนดเอง (Custom)";
    default:
      return "เงื่อนไขไม่ระบุ";
  }
};

const inputCls =
  "w-full min-w-0 max-w-full box-border border border-gray-300 rounded-md py-2 px-3 text-sm font-semibold text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed";

const selectCls =
  "w-full min-w-0 max-w-full box-border border border-gray-300 rounded-md py-2 px-3 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent transition-all disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed";

// ── Main Component ────────────────────────────────────────────────────────────

export const ManageMission = () => {
  const { callApi, configs, updateConfig } = Context();
  const { toast } = useToast();

  const [missions, setMissions] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState({ open: false, list: false });

  // Filter & Search States
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modal / Drawer State
  const [selectedMission, setSelectedMission] = useState(null); // null = closed, object = open
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    getMissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMissions = async () => {
    setLoading((prev) => ({ ...prev, list: true }));
    try {
      const allMissions = await callApi(
        "missions-getAll",
        { limit: 500 },
        { notShowLoading: true }
      );
      const allCoupons = await callApi(
        "coupons-getAllCoupon",
        {
          queries: [
            ["status", "!=", statuses.INACTIVE],
            ["expireAt", ">", Date.now()],
          ],
        },
        { notShowLoading: true }
      );

      setCoupons(allCoupons || []);
      setMissions(allMissions || []);
    } catch (err) {
      toast({
        className: "bg-rose-600 text-white",
        title: "เกิดข้อผิดพลาดในการโหลดข้อมูลภารกิจ",
      });
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  // Master Switch Handler
  const handleToggleMissionSystem = async (nextState) => {
    if (!window.confirm(`ท่านต้องการ${nextState ? "เปิด" : "ปิด"}ระบบทำภารกิจใช่หรือไม่?`)) return;
    setLoading((prev) => ({ ...prev, open: true }));

    const { status } = await callApi(
      "configs-edit",
      {
        id: "general",
        key: "isOpen.mission",
        value: nextState,
      },
      { notShowLoading: true }
    );

    if (status) {
      toast({
        className: "bg-emerald-600 text-white",
        title: `อัปเดตสถานะระบบภารกิจสำเร็จ`,
      });
      updateConfig();
    } else {
      toast({
        className: "bg-rose-600 text-white",
        title: `ไม่สามารถอัปเดตสถานะระบบภารกิจได้`,
      });
    }
    setLoading((prev) => ({ ...prev, open: false }));
  };

  // Filtered and Sorted Missions (Active -> Offline -> Inactive)
  const filteredMissions = useMemo(() => {
    const statusPriority = {
      [statuses.ACTIVE]: 1,
      [statuses.OFFLINE]: 2,
      [statuses.INACTIVE]: 3,
    };

    return [...missions]
      .filter((m) => {
        // Status filter
        if (statusFilter !== "all" && m.status !== statusFilter) {
          return false;
        }
        // Search filter
        if (searchTerm.trim()) {
          const query = searchTerm.toLowerCase();
          const matchName = m.name?.toLowerCase().includes(query);
          const matchReward = m.reward?.name?.toLowerCase().includes(query);
          const matchLastTwo = m.lastTwo?.toString().includes(query);
          const matchType = m.type?.toLowerCase().includes(query);
          return matchName || matchReward || matchLastTwo || matchType;
        }
        return true;
      })
      .sort((a, b) => {
        const priorityA = statusPriority[a.status] || 99;
        const priorityB = statusPriority[b.status] || 99;
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        return (b.createdAt || 0) - (a.createdAt || 0);
      });
  }, [missions, statusFilter, searchTerm]);

  // Statistics
  const stats = useMemo(() => {
    const total = missions.length;
    const active = missions.filter((m) => m.status === statuses.ACTIVE).length;
    const offline = missions.filter((m) => m.status === statuses.OFFLINE).length;
    const inactive = missions.filter((m) => m.status === statuses.INACTIVE).length;
    return { total, active, offline, inactive };
  }, [missions]);

  // Open Create Mission Modal
  const handleOpenCreate = () => {
    setIsNew(true);
    setSelectedMission({
      name: "",
      status: statuses.ACTIVE,
      type: "amountInOneOrder",
      requiredLotto: 1,
      requiredOrder: 1,
      lastTwo: "",
      set: 2,
      requiredSet: 1,
      limitClaim: 0,
      limitPerUser: 1,
      limitPerUserRound: 1,
      expiredAtDate: "",
      info: "",
      reward: null,
      image: "",
    });
  };

  // Open Edit Mission Modal
  const handleOpenEdit = (mission) => {
    setIsNew(false);
    setSelectedMission({
      ...mission,
      requiredLotto: mission.requiredLotto || 0,
      requiredOrder: mission.requiredOrder || 1,
      lastTwo: mission.lastTwo || "",
      set: mission.set || 2,
      requiredSet: mission.requiredSet || 1,
      limitClaim: mission.limitClaim || 0,
      limitPerUser: mission.limitPerUser || 0,
      limitPerUserRound: mission.limitPerUserRound || 0,
      expiredAtDate: mission.expiredAtDate || "",
      info: mission.info || "",
    });
  };

  // Copy / Duplicate Mission
  const handleCopyMission = (mission) => {
    setIsNew(true);
    setSelectedMission({
      ...mission,
      id: undefined,
      name: `${mission.name || ""} (คัดลอก)`.trim(),
      status: mission.status || statuses.ACTIVE,
      requiredLotto: mission.requiredLotto || 0,
      requiredOrder: mission.requiredOrder || 1,
      lastTwo: mission.lastTwo || "",
      set: mission.set || 2,
      requiredSet: mission.requiredSet || 1,
      limitClaim: mission.limitClaim || 0,
      limitPerUser: mission.limitPerUser || 0,
      limitPerUserRound: mission.limitPerUserRound || 0,
      expiredAtDate: mission.expiredAtDate || "",
      info: mission.info || "",
      reward: mission.reward || null,
      image: mission.image || "",
    });
    toast({
      className: "bg-blue text-white",
      title: "คัดลอกข้อมูลภารกิจเรียบร้อย",
      description: "สามารถแก้ไขชื่อหรือเงื่อนไข แล้วกดบันทึกเป็นภารกิจใหม่ได้ทันที",
    });
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-12" id="main">
      <Header
        header="Mission"
        subHeader="จัดการภารกิจและเงื่อนไขการรับรางวัล"
        icon={<GrBike className="text-blue" />}
      />

      {/* ── Top Dashboard Cards & Master Switch ─────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
        {/* Master Switch Card */}
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">

              <p className="text-sm font-bold text-gray-800">ระบบภารกิจ</p>
            </div>

          </div>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs font-semibold text-gray-500">
              {configs?.isOpen?.mission ? "เปิดให้ทำภารกิจในระบบ" : "ปิดการทำภารกิจชั่วคราว"}
            </span>
            <button
              type="button"
              disabled={loading.open || !configs}
              onClick={() => handleToggleMissionSystem(!configs?.isOpen?.mission)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${configs?.isOpen?.mission ? "bg-emerald-500" : "bg-gray-300"
                }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${configs?.isOpen?.mission ? "translate-x-5" : "translate-x-0"
                  }`}
              />
            </button>
          </div>
        </div>

        {/* Stat: Total */}
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500">ภารกิจทั้งหมด</p>
            <p className="text-2xl font-bold text-navy mt-1">{stats.total}</p>
          </div>
          <div className="w-10 h-10 rounded-md bg-blue/10 text-blue flex items-center justify-center text-lg">
            <FaLayerGroup />
          </div>
        </div>

        {/* Stat: Active */}
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-emerald-600">เปิดใช้งาน (Active)</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">{stats.active}</p>
          </div>
          <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center text-lg">
            <FaCircleCheck />
          </div>
        </div>

        {/* Stat: Offline / Inactive */}
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-200 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500">ออฟไลน์ / ปิดใช้งาน</p>
            <p className="text-2xl font-bold text-gray-700 mt-1">
              {stats.offline + stats.inactive}{" "}
              <span className="text-xs font-medium text-gray-400">
                ({stats.offline} ออฟไลน์ / {stats.inactive} ปิด)
              </span>
            </p>
          </div>
          <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center text-lg">
            <FaCircleExclamation />
          </div>
        </div>
      </div>

      {/* ── Main Content Area: Table & Search ──────────────────────────────────── */}
      <div className="bg-white w-full rounded-md shadow-sm mt-4 border border-gray-200 overflow-hidden">
        {/* Controls Toolbar */}
        <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Status Tabs */}
          <div className="flex items-center gap-1.5 bg-gray-100 p-1 rounded-md">
            <button
              onClick={() => setStatusFilter("all")}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${statusFilter === "all"
                ? "bg-white text-navy shadow-sm"
                : "text-gray-500 hover:text-gray-900"
                }`}
            >
              ทั้งหมด ({stats.total})
            </button>
            <button
              onClick={() => setStatusFilter(statuses.ACTIVE)}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${statusFilter === statuses.ACTIVE
                ? "bg-emerald-500 text-white shadow-sm"
                : "text-gray-500 hover:text-emerald-700"
                }`}
            >
              เปิดใช้งาน ({stats.active})
            </button>
            <button
              onClick={() => setStatusFilter(statuses.OFFLINE)}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${statusFilter === statuses.OFFLINE
                ? "bg-[#FAB12F] text-white shadow-sm"
                : "text-gray-500 hover:text-amber-700"
                }`}
            >
              เฉพาะแอดมิน ({stats.offline})
            </button>
            <button
              onClick={() => setStatusFilter(statuses.INACTIVE)}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${statusFilter === statuses.INACTIVE
                ? "bg-rose-500 text-white shadow-sm"
                : "text-gray-500 hover:text-rose-700"
                }`}
            >
              ปิดใช้งาน ({stats.inactive})
            </button>
          </div>

          {/* Search Box & Action Buttons */}
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <FaMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="ค้นหาชื่อภารกิจ, รางวัล, เลขท้าย..."
                className="pl-8 pr-4 py-2 border border-gray-300 rounded-md text-sm font-semibold placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue focus:border-transparent w-64 box-border"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                >
                  <FaXmark />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={getMissions}
              title="รีเฟรชข้อมูล"
              className="p-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <FaArrowRotateRight className={`text-sm ${loading.list ? "animate-spin" : ""}`} />
            </button>

            <button
              type="button"
              onClick={handleOpenCreate}
              className="bg-blue hover:opacity-90 py-2.5 px-4 text-sm font-bold rounded-md text-white shadow-sm flex items-center gap-2 transition-all"
            >
              <FaPlus className="text-xs" /> สร้างภารกิจใหม่
            </button>
          </div>
        </div>

        {/* Table */}
        <Table>
          <TableHeader className="bg-gray-50 border-b border-gray-200">
            <TableRow>
              <TableHead className="w-12 text-center text-xs font-bold text-gray-500">#</TableHead>
              <TableHead className="text-left text-xs font-bold text-gray-500">ภารกิจ</TableHead>
              <TableHead className="text-left text-xs font-bold text-gray-500">เงื่อนไขการทำ</TableHead>
              <TableHead className="text-left text-xs font-bold text-gray-500">ของรางวัล (Coupon)</TableHead>
              <TableHead className="text-center text-xs font-bold text-gray-500">โควตา / สิทธิ์</TableHead>
              <TableHead className="text-center text-xs font-bold text-gray-500">สถานะ</TableHead>
              <TableHead className="w-24 text-center text-xs font-bold text-gray-500">จัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading.list ? (
              <TableRow>
                <TableCell colSpan={7} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                    <div className="w-8 h-8 border-4 border-blue border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm font-semibold">กำลังโหลดข้อมูลภารกิจ...</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredMissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                    <GrBike className="text-4xl text-gray-300" />
                    <p className="text-sm font-semibold">ไม่พบข้อมูลภารกิจ</p>
                    {searchTerm && (
                      <p className="text-xs text-gray-400">
                        ลองเปลี่ยนคำค้นหา หรือ{" "}
                        <button
                          onClick={() => setSearchTerm("")}
                          className="text-blue underline font-semibold"
                        >
                          ล้างคำค้นหา
                        </button>
                      </p>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredMissions.map((mission, index) => {
                const missionTypeObj = MISSION_TYPES.find((t) => t.value === mission.type);
                return (
                  <TableRow key={mission.id || index} className="hover:bg-gray-50/80 transition-colors">
                    <TableCell className="text-center font-semibold text-gray-500 text-sm">
                      {index + 1}
                    </TableCell>

                    {/* Mission Info & Thumbnail */}
                    <TableCell>
                      <div className="flex items-center gap-3 py-1">
                        <div className="w-12 h-12 rounded-md bg-gray-100 border border-gray-200 overflow-hidden shrink-0 flex items-center justify-center">
                          {mission.image ? (
                            <img
                              src={mission.image}
                              alt={mission.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <GrBike className="text-xl text-gray-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-gray-800 text-sm hover:text-blue transition-colors">
                            {mission.name}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue/10 text-blue text-[11px] font-semibold">
                              <span>{missionTypeObj?.icon || "🎯"}</span>
                              <span>{missionTypeObj?.label || mission.type}</span>
                            </span>
                            {mission.expiredAtDate && (
                              <span className="text-[11px] text-gray-400 font-medium">
                                หมดอายุ: {mission.expiredAtDate}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>

                    {/* Condition Summary */}
                    <TableCell>
                      <div className="max-w-xs">
                        <p className="text-sm font-semibold text-gray-700">
                          {getMissionConditionSummary(mission)}
                        </p>
                        {mission.info && (
                          <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">
                            {mission.info}
                          </p>
                        )}
                      </div>
                    </TableCell>

                    {/* Reward */}
                    <TableCell>
                      {mission.reward ? (
                        <div className="flex items-start gap-2">
                          <div className="p-1.5 rounded-md bg-emerald-50 text-emerald-600 mt-0.5 shrink-0">
                            <FaGift className="text-xs" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-800">
                              {mission.reward.name}
                            </p>
                            <p className="text-[11px] text-gray-400 font-medium">
                              {mission.reward.expireAt ? (
                                <>
                                  หมดอายุ{" "}
                                  {new Date(mission.reward.expireAt).toLocaleDateString("th-TH", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}{" "}
                                  น.
                                </>
                              ) : (
                                "ไม่มีวันหมดอายุ"
                              )}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs font-semibold text-rose-500 bg-rose-50 px-2 py-1 rounded-md">
                          ยังไม่ได้ผูกคูปองรางวัล
                        </span>
                      )}
                    </TableCell>

                    {/* Quota & Limits */}
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-sm font-bold text-gray-800">
                          {mission.limitClaim > 0
                            ? `${Number(mission.limitClaim).toLocaleString()} สิทธิ์`
                            : "ไม่จำกัด"}
                        </span>
                        <span className="text-[11px] text-gray-400">
                          {mission.limitPerUser > 0 ? `${mission.limitPerUser} ครั้ง/คน` : "ไม่จำกัด/คน"}
                          {mission.limitPerUserRound > 0 ? ` (${mission.limitPerUserRound}/งวด)` : ""}
                        </span>
                      </div>
                    </TableCell>

                    {/* Status Badge */}
                    <TableCell className="text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold ${mission.status === statuses.ACTIVE
                          ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                          : mission.status === statuses.OFFLINE
                            ? "bg-amber-50 text-amber-600 border border-amber-200"
                            : "bg-rose-50 text-rose-600 border border-rose-200"
                          }`}
                      >
                        {constant.statusesTranslate[mission.status] || mission.status}
                      </span>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleCopyMission(mission)}
                          title="คัดลอกภารกิจ (Duplicate)"
                          className="p-1.5 text-gray-500 hover:text-blue hover:bg-blue/10 rounded-md transition-colors"
                        >
                          <FaCopy className="text-sm" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(mission)}
                          title="แก้ไขภารกิจ"
                          className="p-1.5 text-blue hover:bg-blue/10 rounded-md transition-colors"
                        >
                          <FaPenToSquare className="text-sm" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Create / Edit Mission Modal ────────────────────────────────────────── */}
      {selectedMission && (
        <MissionModal
          isNew={isNew}
          mission={selectedMission}
          coupons={coupons}
          onClose={() => setSelectedMission(null)}
          onCopy={(missionToCopy) => handleCopyMission(missionToCopy)}
          onSuccess={() => {
            setSelectedMission(null);
            getMissions();
          }}
        />
      )}
    </div>
  );
};

// ── Mission Create/Edit Modal Component ───────────────────────────────────────

const MissionModal = ({ isNew, mission: initialMission, coupons, onClose, onCopy, onSuccess }) => {
  const { callApi } = Context();
  const { toast } = useToast();

  const [form, setForm] = useState({ ...initialMission });
  const [previewImage, setPreviewImage] = useState(initialMission.image || "");
  const [submitting, setSubmitting] = useState(false);
  const [couponSearch, setCouponSearch] = useState("");

  const activeCoupons = useMemo(() => {
    if (!coupons) return [];
    return [...coupons]
      .filter((coupon) => coupon.status !== statuses.INACTIVE)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [coupons]);

  const filteredCoupons = useMemo(() => {
    if (!couponSearch.trim()) return activeCoupons;
    const q = couponSearch.toLowerCase();
    return activeCoupons.filter((c) => c.name?.toLowerCase().includes(q) || c.id?.includes(q));
  }, [activeCoupons, couponSearch]);

  const handleCouponSelect = (coupon) => {
    setForm((prev) => ({
      ...prev,
      reward: coupon
        ? {
          id: coupon.id,
          name: coupon.name,
          expireAt: coupon.expireAt,
        }
        : null,
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await resizeImage(file);
    if (base64) {
      const previewUrl = URL.createObjectURL(file);
      setPreviewImage(previewUrl);
      setForm((prev) => ({ ...prev, image: base64 }));
    } else {
      toast({
        className: "bg-rose-600 text-white",
        title: "อัปโหลดได้เฉพาะไฟล์รูปภาพ (jpg หรือ png)",
      });
    }
  };

  const handleRemoveImage = () => {
    setPreviewImage("");
    setForm((prev) => ({ ...prev, image: "" }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!form.name?.trim()) {
      toast({
        className: "bg-rose-600 text-white",
        title: "กรุณาระบุชื่อภารกิจ",
      });
      return;
    }
    if (!form.type) {
      toast({
        className: "bg-rose-600 text-white",
        title: "กรุณาเลือกประเภทภารกิจ",
      });
      return;
    }
    if (!form.reward?.id) {
      toast({
        className: "bg-rose-600 text-white",
        title: "กรุณาเลือกคูปองรางวัลสำหรับภารกิจ",
      });
      return;
    }

    // Specific type validation
    if (["lastTwoInOneOrder", "lastTwoInMultipleOrders"].includes(form.type) && !form.lastTwo) {
      toast({
        className: "bg-rose-600 text-white",
        title: "กรุณาระบุเลขท้าย 2 ตัว (00 - 99)",
      });
      return;
    }

    setSubmitting(true);
    try {
      const endpoint = isNew ? "missions-createMission" : "missions-editMission";
      const payload = {
        ...form,
        limitClaim: Number(form.limitClaim) || 0,
        limitPerUser: Number(form.limitPerUser) || 0,
        limitPerUserRound: Number(form.limitPerUserRound) || 0,
        requiredLotto: Number(form.requiredLotto) || 0,
        requiredOrder: Number(form.requiredOrder) || 1,
        requiredSet: Number(form.requiredSet) || 0,
        set: Number(form.set) || 0,
      };

      const result = await callApi(endpoint, payload, { notShowLoading: true });
      if (result.status) {
        toast({
          className: "bg-emerald-600 text-white",
          title: (
            <div className="flex items-center gap-2">
              <FaCircleCheck /> {isNew ? "สร้างภารกิจสำเร็จ" : "บันทึกข้อมูลภารกิจสำเร็จ"}
            </div>
          ),
        });
        onSuccess();
      } else {
        toast({
          className: "bg-rose-600 text-white",
          title: "ทำรายการไม่สำเร็จ",
          description: result.message || "เกิดข้อผิดพลาดในการบันทึก",
        });
      }
    } catch (err) {
      toast({
        className: "bg-rose-600 text-white",
        title: "เกิดข้อผิดพลาดในระบบ",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("ยืนยันต้องการลบภารกิจนี้ใช่หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้")) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await callApi(
        "missions-deleteMission",
        { id: form.id },
        { notShowLoading: true }
      );
      if (result.status) {
        toast({
          className: "bg-emerald-600 text-white",
          title: "ลบภารกิจสำเร็จ",
        });
        onSuccess();
      } else {
        toast({
          className: "bg-rose-600 text-white",
          title: "ไม่สามารถลบภารกิจได้",
          description: result.message,
        });
      }
    } catch (err) {
      toast({
        className: "bg-rose-600 text-white",
        title: "เกิดข้อผิดพลาดในการลบภารกิจ",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex justify-center items-center p-4">
      <div className="bg-white rounded-md shadow-2xl border border-gray-200 w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white shrink-0 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue/10 text-blue flex items-center justify-center text-lg">
              {isNew ? <FaPlus /> : <FaPenToSquare />}
            </div>
            <div>
              <p className="text-xl font-bold text-navy">
                {isNew ? "สร้างภารกิจใหม่" : "แก้ไขภารกิจ"}
              </p>
              <p className="text-xs font-semibold text-gray-500">
                กำหนดเงื่อนไข, ของรางวัล และรูปแบบการทำภารกิจ
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <FaXmark className="text-lg" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 flex flex-col lg:flex-row gap-6 bg-gray-50/50 min-w-0">
          {/* Left Form: Flex-1 with min-w-0 */}
          <div className="flex-1 min-w-0 flex flex-col gap-6">
            {/* Section 1: ข้อมูลทั่วไป */}
            <div className="bg-white p-5 rounded-md border border-gray-200 shadow-sm min-w-0">
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100">
                <span className="w-6 h-6 rounded-md bg-blue text-white text-xs font-bold flex items-center justify-center shrink-0">
                  1
                </span>
                <p className="text-base font-bold text-gray-800">ข้อมูลพื้นฐานภารกิจ</p>
              </div>

              <div className="flex flex-col gap-4 min-w-0">
                {/* Name */}
                <div className="w-full min-w-0">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    ชื่อภารกิจ <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="เช่น นักล่าเลขท้าย 89, ซื้อสลากชุด 5 ใบ"
                    value={form.name || ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>

                {/* Status Selection Buttons */}
                <div className="w-full min-w-0">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">สถานะภารกิจ</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 min-w-0">
                    <button
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, status: statuses.ACTIVE }))}
                      className={`py-2 px-3 rounded-md text-xs font-bold border transition-all flex items-center justify-center gap-1.5 min-w-0 truncate ${form.status === statuses.ACTIVE
                        ? "bg-emerald-500 border-emerald-500 text-white shadow-sm"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      <FaCircleCheck className="shrink-0" /> <span className="truncate">เปิดใช้งาน (Active)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, status: statuses.OFFLINE }))}
                      className={`py-2 px-3 rounded-md text-xs font-bold border transition-all flex items-center justify-center gap-1.5 min-w-0 truncate ${form.status === statuses.OFFLINE
                        ? "bg-[#FAB12F] border-[#FAB12F] text-white shadow-sm"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      <FaCircleExclamation className="shrink-0" /> <span className="truncate">เฉพาะแอดมิน (Offline)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, status: statuses.INACTIVE }))}
                      className={`py-2 px-3 rounded-md text-xs font-bold border transition-all flex items-center justify-center gap-1.5 min-w-0 truncate ${form.status === statuses.INACTIVE
                        ? "bg-rose-500 border-rose-500 text-white shadow-sm"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      <FaXmark className="shrink-0" /> <span className="truncate">ปิดใช้งาน (Inactive)</span>
                    </button>
                  </div>
                </div>

                {/* Info / Description */}
                <div className="w-full min-w-0">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">
                    คำอธิบาย / รายละเอียดเพิ่มเติม (แสดงให้ลูกค้าอ่าน)
                  </label>
                  <textarea
                    rows={2}
                    className={inputCls}
                    placeholder="ระบุข้อความชี้แจงเงื่อนไขสั้นๆ สำหรับลูกค้า..."
                    value={form.info || ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, info: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Section 2: เงื่อนไขและรูปแบบภารกิจ */}
            <div className="bg-white p-5 rounded-md border border-gray-200 shadow-sm min-w-0">
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100">
                <span className="w-6 h-6 rounded-md bg-blue text-white text-xs font-bold flex items-center justify-center shrink-0">
                  2
                </span>
                <p className="text-base font-bold text-gray-800">รูปแบบ & เงื่อนไขภารกิจ</p>
              </div>

              {/* Mission Type Visual Selector */}
              <div className="mb-4 min-w-0">
                <label className="block text-xs font-bold text-gray-700 mb-2">
                  เลือกประเภทภารกิจ <span className="text-rose-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 min-w-0">
                  {MISSION_TYPES.map((type) => {
                    const isSelected = form.type === type.value;
                    return (
                      <div
                        key={type.value}
                        onClick={() => setForm((prev) => ({ ...prev, type: type.value }))}
                        className={`p-3 rounded-md border cursor-pointer transition-all flex items-start gap-3 min-w-0 ${isSelected
                          ? "bg-blue/5 border-blue ring-1 ring-blue"
                          : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                          }`}
                      >
                        <span className="text-xl shrink-0 mt-0.5">{type.icon}</span>
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs font-bold truncate ${isSelected ? "text-blue" : "text-gray-800"}`}>
                            {type.label}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5 leading-snug line-clamp-2">
                            {type.desc}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dynamic Specific Condition Inputs */}
              <div className="bg-gray-50 p-4 rounded-md border border-gray-200 space-y-4 min-w-0">
                <p className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                  <TbSparkles className="text-blue text-sm shrink-0" /> ตั้งค่าเงื่อนไขเฉพาะของรูปแบบที่เลือก
                </p>

                {/* 1. amountInOneOrder */}
                {form.type === "amountInOneOrder" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                    <div className="min-w-0">
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        จำนวนสลากที่ต้องซื้อ (ใบ) <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="เช่น 1, 5, 10"
                        value={form.requiredLotto || ""}
                        onChange={(e) =>
                          validate.digitOnly.test(e.target.value) &&
                          setForm((prev) => ({ ...prev, requiredLotto: e.target.value }))
                        }
                      />
                      <p className="text-[11px] text-gray-400 mt-1">ต้องสั่งซื้อให้ครบใน 1 ออเดอร์</p>
                    </div>
                  </div>
                )}

                {/* 2. lastTwoInOneOrder */}
                {form.type === "lastTwoInOneOrder" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                    <div className="min-w-0">
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        เลขท้าย 2 ตัวที่กำหนด (00-99) <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={2}
                        className={inputCls}
                        placeholder="เช่น 89"
                        value={form.lastTwo || ""}
                        onChange={(e) =>
                          validate.digitOnly.test(e.target.value) &&
                          setForm((prev) => ({ ...prev, lastTwo: e.target.value }))
                        }
                      />
                      <p className="text-[11px] text-gray-400 mt-1">ระบุเลข 2 หลัก เช่น 05, 99</p>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        จำนวนใบที่ต้องซื้อ (ใบ) <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="เช่น 1, 2"
                        value={form.requiredLotto || ""}
                        onChange={(e) =>
                          validate.digitOnly.test(e.target.value) &&
                          setForm((prev) => ({ ...prev, requiredLotto: e.target.value }))
                        }
                      />
                      <p className="text-[11px] text-gray-400 mt-1">ใน 1 คำสั่งซื้อ</p>
                    </div>
                  </div>
                )}

                {/* 3. lastTwoInMultipleOrders */}
                {form.type === "lastTwoInMultipleOrders" && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
                    <div className="min-w-0">
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        เลขท้าย 2 ตัว (00-99) <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={2}
                        className={inputCls}
                        placeholder="เช่น 89"
                        value={form.lastTwo || ""}
                        onChange={(e) =>
                          validate.digitOnly.test(e.target.value) &&
                          setForm((prev) => ({ ...prev, lastTwo: e.target.value }))
                        }
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        จำนวนใบรวม (ใบ) <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="เช่น 5"
                        value={form.requiredLotto || ""}
                        onChange={(e) =>
                          validate.digitOnly.test(e.target.value) &&
                          setForm((prev) => ({ ...prev, requiredLotto: e.target.value }))
                        }
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        จำนวนออเดอร์ (ออเดอร์) <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="เช่น 3"
                        value={form.requiredOrder || ""}
                        onChange={(e) =>
                          validate.digitOnly.test(e.target.value) &&
                          setForm((prev) => ({ ...prev, requiredOrder: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                )}

                {/* 4. setInMultipleOrder */}
                {form.type === "setInMultipleOrder" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                    <div className="min-w-0">
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        ประเภทชุดสลาก <span className="text-rose-500">*</span>
                      </label>
                      <select
                        className={selectCls}
                        value={form.set || 2}
                        onChange={(e) => setForm((prev) => ({ ...prev, set: Number(e.target.value) }))}
                      >
                        {SET_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="min-w-0">
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        จำนวนชุดที่ต้องซื้อ (ชุด) <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="เช่น 2, 5"
                        value={form.requiredSet || ""}
                        onChange={(e) =>
                          validate.digitOnly.test(e.target.value) &&
                          setForm((prev) => ({ ...prev, requiredSet: e.target.value }))
                        }
                      />
                    </div>
                    <div className="sm:col-span-2 text-xs font-semibold text-blue bg-blue/10 p-2.5 rounded-md min-w-0">
                      คำนวณจำนวนสลากรวม:{" "}
                      <span className="font-bold">
                        {(Number(form.set) || 2) * (Number(form.requiredSet) || 0)} ใบ
                      </span>{" "}
                      (ชุด {form.set || 2} ใบ × {form.requiredSet || 0} ชุด)
                    </div>
                  </div>
                )}

                {/* 5. Custom */}
                {form.type === "custom" && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 font-medium leading-relaxed min-w-0">
                    ℹ️ ภารกิจประเภทนี้แอดมินหรือระบบจะเป็นผู้ตรวจสอบและอนุมัติด้วยตนเอง
                    กรุณากรอกรายละเอียดเงื่อนไขให้ชัดเจนในช่องคำอธิบายด้านบน
                  </div>
                )}

                {/* Live Rule Summary Display */}
                <div className="p-3 bg-white rounded-md border border-gray-200 flex items-center justify-between min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-base shrink-0">💡</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-gray-700">สรุปเงื่อนไขการทำภารกิจ</p>
                      <p className="text-xs font-semibold text-blue mt-0.5 break-words">
                        {getMissionConditionSummary(form)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: ของรางวัล & คูปอง */}
            <div className="bg-white p-5 rounded-md border border-gray-200 shadow-sm min-w-0">
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100">
                <span className="w-6 h-6 rounded-md bg-blue text-white text-xs font-bold flex items-center justify-center shrink-0">
                  3
                </span>
                <p className="text-base font-bold text-gray-800">
                  ของรางวัลตอบแทน (Coupon Reward) <span className="text-rose-500">*</span>
                </p>
              </div>

              {/* Coupon Selector with Search */}
              <div className="space-y-3 min-w-0">
                <div className="relative w-full min-w-0">
                  <FaMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                  <input
                    type="text"
                    placeholder="พิมพ์ค้นหาคูปองรางวัล..."
                    value={couponSearch}
                    onChange={(e) => setCouponSearch(e.target.value)}
                    className="pl-8 pr-4 py-2 border border-gray-300 rounded-md text-xs font-semibold placeholder:text-gray-400 w-full min-w-0 max-w-full box-border focus:outline-none focus:ring-2 focus:ring-blue"
                  />
                </div>

                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100 bg-white min-w-0">
                  {filteredCoupons.length === 0 ? (
                    <div className="p-4 text-center text-xs font-semibold text-gray-400">
                      ไม่พบคูปองที่พร้อมใช้งาน
                    </div>
                  ) : (
                    filteredCoupons.map((coupon) => {
                      const isSelected = form.reward?.id === coupon.id;
                      return (
                        <div
                          key={coupon.id}
                          onClick={() => handleCouponSelect(coupon)}
                          className={`p-2.5 px-3 flex items-center justify-between cursor-pointer transition-colors text-xs min-w-0 ${isSelected
                            ? "bg-emerald-50 text-emerald-900 font-bold"
                            : "hover:bg-gray-50 text-gray-700"
                            }`}
                        >
                          <div className="flex items-center gap-2 min-w-0 truncate">
                            <FaTicket className={`shrink-0 ${isSelected ? "text-emerald-600" : "text-gray-400"}`} />
                            <span className="truncate">{coupon.name}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-[11px] text-gray-400">
                              {coupon.expireAt
                                ? `หมดอายุ: ${new Date(coupon.expireAt).toLocaleDateString("th-TH")}`
                                : "ไม่มีวันหมดอายุ"}
                            </span>
                            {isSelected && <FaCheck className="text-emerald-600 font-bold" />}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Selected Coupon Card */}
                {form.reward?.id && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md flex items-center justify-between min-w-0">
                    <div className="flex items-center gap-2.5 min-w-0 truncate">
                      <div className="w-8 h-8 rounded-md bg-emerald-500 text-white flex items-center justify-center text-sm shrink-0">
                        <FaGift />
                      </div>
                      <div className="min-w-0 truncate">
                        <p className="text-xs font-bold text-emerald-900 truncate">
                          คูปองที่เลือก: {form.reward.name}
                        </p>
                        <p className="text-[11px] text-emerald-700">
                          {form.reward.expireAt
                            ? `หมดอายุ ${new Date(form.reward.expireAt).toLocaleDateString("th-TH")}`
                            : "ไม่มีวันหมดอายุ"}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCouponSelect(null)}
                      className="text-xs text-rose-500 hover:text-rose-700 font-bold p-1 shrink-0"
                    >
                      ยกเลิก
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Section 4: โควตาสิทธิ์และระยะเวลา */}
            <div className="bg-white p-5 rounded-md border border-gray-200 shadow-sm min-w-0">
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100">
                <span className="w-6 h-6 rounded-md bg-blue text-white text-xs font-bold flex items-center justify-center shrink-0">
                  4
                </span>
                <p className="text-base font-bold text-gray-800">จำกัดสิทธิ์ & ระยะเวลา</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
                {/* Total Quota */}
                <div className="min-w-0">
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    จำกัดสิทธิ์ทั้งหมด (สิทธิ์)
                  </label>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="0 หรือ เว้นว่าง = ไม่จำกัด"
                    value={form.limitClaim ?? ""}
                    onChange={(e) =>
                      validate.digitOnly.test(e.target.value) &&
                      setForm((prev) => ({ ...prev, limitClaim: e.target.value }))
                    }
                  />
                  <p className="text-[11px] text-gray-400 mt-1">จำนวนผู้รับรางวัลสูงสุดของภารกิจนี้</p>
                </div>

                {/* Per User */}
                <div className="min-w-0">
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    จำกัดต่อผู้ใช้ ตลอดชีพ (ครั้ง/คน)
                  </label>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="เช่น 1"
                    value={form.limitPerUser ?? ""}
                    onChange={(e) =>
                      validate.digitOnly.test(e.target.value) &&
                      setForm((prev) => ({ ...prev, limitPerUser: e.target.value }))
                    }
                  />
                </div>

                {/* Per User Round */}
                <div className="min-w-0">
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    จำกัดต่อผู้ใช้ ต่องวด (ครั้ง/คน/งวด)
                  </label>
                  <input
                    type="text"
                    className={inputCls}
                    placeholder="เช่น 1"
                    value={form.limitPerUserRound ?? ""}
                    onChange={(e) =>
                      validate.digitOnly.test(e.target.value) &&
                      setForm((prev) => ({ ...prev, limitPerUserRound: e.target.value }))
                    }
                  />
                </div>

                {/* Expired Date */}
                <div className="min-w-0">
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    วันหมดอายุภารกิจ
                  </label>
                  <input
                    type="date"
                    className={inputCls}
                    value={form.expiredAtDate || ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, expiredAtDate: e.target.value }))}
                  />
                  <p className="text-[11px] text-gray-400 mt-1">
                    {form.expiredAtDate ? `สิ้นสุดวันที่ ${form.expiredAtDate}` : "ไม่มีวันหมดอายุ"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Image Upload & Live Preview (Fixed width / 340px) */}
          <div className="w-full lg:w-80 shrink-0 flex flex-col gap-6 min-w-0">
            {/* Image Upload Box */}
            <div className="bg-white p-5 rounded-md border border-gray-200 shadow-sm min-w-0">
              <p className="text-sm font-bold text-gray-800 mb-3">รูปภาพภารกิจ</p>

              <label
                htmlFor="missionImageInput"
                className={`w-full h-44 rounded-md border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all overflow-hidden relative group min-w-0 ${previewImage ? "border-transparent bg-gray-900" : "border-gray-300 hover:border-blue bg-gray-50"
                  }`}
              >
                {previewImage ? (
                  <>
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="w-full h-full object-cover group-hover:opacity-75 transition-opacity"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 text-white text-xs font-bold gap-2">
                      <TbPhotoPlus className="text-lg" /> เปลี่ยนรูปภาพ
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-400 p-4 text-center">
                    <TbPhotoPlus className="text-3xl text-blue mb-1" />
                    <p className="text-xs font-bold text-gray-700">คลิกเพื่ออัปโหลดรูปภาพ</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">JPG หรือ PNG (สัดส่วน 1:1 หรือ 4:3)</p>
                  </div>
                )}
                <input
                  id="missionImageInput"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>

              {previewImage && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="text-xs text-rose-500 hover:text-rose-700 font-bold flex items-center gap-1"
                  >
                    <FaTrashCan className="text-[10px]" /> ลบรูปภาพ
                  </button>
                </div>
              )}
            </div>

            {/* Live Customer Card Preview */}
            <div className="bg-white p-5 rounded-md border border-gray-200 shadow-sm sticky top-20 min-w-0">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-gray-800">ตัวอย่างการแสดงผลบนแอปลูกค้า</p>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0">
                  LIVE PREVIEW
                </span>
              </div>

              {/* Mock Mobile Card */}
              <div className="border border-gray-200 rounded-md p-3.5 bg-white shadow-sm space-y-3 min-w-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-14 h-14 rounded-md bg-gray-100 border border-gray-200 shrink-0 overflow-hidden flex items-center justify-center">
                    {previewImage ? (
                      <img src={previewImage} alt="Mission" className="w-full h-full object-cover" />
                    ) : (
                      <GrBike className="text-2xl text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-gray-800 truncate">
                      {form.name || "ชื่อภารกิจของคุณ"}
                    </p>
                    <p className="text-[11px] text-blue font-semibold mt-0.5 line-clamp-1">
                      {getMissionConditionSummary(form)}
                    </p>
                    <span className="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 truncate max-w-full">
                      {form.reward?.name || "ยังไม่ได้เลือกรางวัล"}
                    </span>
                  </div>
                </div>

                {/* Mock Progress */}
                <div className="space-y-1 pt-2 border-t border-gray-100 min-w-0">
                  <div className="flex justify-between text-[11px] font-semibold text-gray-500">
                    <span>ความคืบหน้า</span>
                    <span className="text-blue font-bold">0 / {form.requiredLotto || 1} ใบ</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="w-1/3 h-full bg-blue rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between shrink-0 sticky bottom-0 z-10">
          <div className="flex items-center gap-2">
            {!isNew && (
              <>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleDelete}
                  className="py-2.5 px-3 text-xs font-bold rounded-md text-rose-600 hover:bg-rose-50 border border-rose-200 flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  <FaTrashCan /> ลบภารกิจนี้
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => onCopy && onCopy(form)}
                  className="py-2.5 px-3 text-xs font-bold rounded-md text-gray-700 hover:bg-gray-100 border border-gray-300 flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  <FaCopy /> คัดลอกเป็นภารกิจใหม่
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              disabled={submitting}
              onClick={onClose}
              className="py-2.5 px-4 text-sm font-semibold rounded-md text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={handleSubmit}
              className="bg-blue hover:opacity-90 py-2.5 px-6 text-sm font-bold rounded-md text-white shadow-sm flex items-center gap-2 transition-all disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  กำลังบันทึก...
                </>
              ) : (
                <>
                  <FaCheck /> {isNew ? "สร้างภารกิจ" : "บันทึกการเปลี่ยนแปลง"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageMission;
